/**
 * W1 workspace non-component helpers — the dataset-tab model (which sections
 * apply + where each links), plan-derived KPIs, and the plan-curve geometry.
 * JS-only (no JSX) so the component file stays fast-refresh-clean, mirroring
 * W0's `planCatalog.ts`.
 */
import type { PlanDatasets, ProgramaStep, AttributeRow } from "@api/types";
import type { SectionKey } from "@lib/eventEnrichment";
import type { MessageKey } from "@i18n/messages";
import { asArray } from "@lib/asArray";

/**
 * A dataset tab in the AFE hub. `route` is the sibling authoring screen this tab
 * links to (W2–W6); `null` means the section renders **inline** in the hub
 * (objetivo / atributos, which have no dedicated screen). Other agents own the
 * routed screens, so W1 never embeds them — it links.
 */
export interface DatasetTab {
  section: SectionKey;
  labelKey: MessageKey;
  /** Sibling route, or null for an inline hub panel. */
  route: string | null;
}

/** Section → its i18n label + sibling route (null = inline hub panel). */
const SECTION_TAB: Record<SectionKey, { labelKey: MessageKey; route: string | null }> = {
  objetivo: { labelKey: "ws.tab.objetivo", route: null },
  programa: { labelKey: "ws.tab.programa", route: "/program" },
  hole_plan: { labelKey: "ws.tab.holePlan", route: "/hole-plan" },
  casing: { labelKey: "ws.tab.casing", route: "/casing" },
  plan_costos: { labelKey: "ws.tab.cost", route: "/cost" },
  atributos: { labelKey: "ws.tab.atributos", route: null },
  fractura: { labelKey: "ws.tab.fractura", route: "/program" },
};

/** Build the ordered dataset tabs for an event's applicable sections. */
export function datasetTabs(sections: SectionKey[]): DatasetTab[] {
  return sections.map((section) => ({ section, ...SECTION_TAB[section] }));
}

/** A single derived headline KPI for the plan (value + i18n label). */
export interface PlanKpi {
  value: string;
  labelKey: MessageKey;
}

/** Sum a numeric programa column, skipping nulls; null if the program is empty. */
function sumProgram(rows: ProgramaStep[], pick: (r: ProgramaStep) => number | null): number | null {
  if (rows.length === 0) return null;
  return rows.reduce((acc, r) => acc + (pick(r) ?? 0), 0);
}

/** Read an objetivo/atributos attribute value by (case-insensitive) name. */
function attr(datasets: PlanDatasets | undefined, name: string): string | null {
  const needle = name.toLowerCase();
  const rows = [
    ...asArray<AttributeRow>(datasets?.objetivo),
    ...asArray<AttributeRow>(datasets?.atributos),
  ];
  const hit = rows.find((a) => a.name.toLowerCase().includes(needle));
  return hit?.value ?? null;
}

/**
 * The four headline KPIs the hub shows: target duration (Σ DOL), target cost
 * (authorized total or Σ programa amount), the planned rig, and the deepest MD.
 * Values are already-formatted strings so the component stays presentational.
 */
export function planKpis(
  datasets: PlanDatasets | undefined,
  authorizedTotal: number | null,
  fmtNumber: (n: number | null) => string,
  fmtMoney: (n: number | null) => string,
): PlanKpi[] {
  const program = asArray<ProgramaStep>(datasets?.programa);
  const durationDol = sumProgram(program, (r) => r.dol);
  const cost = authorizedTotal ?? sumProgram(program, (r) => r.amount_usd);
  const finalMd = program.reduce<number | null>(
    (deepest, r) => (r.depth_until != null && (deepest == null || r.depth_until > deepest) ? r.depth_until : deepest),
    null,
  );
  const rig = attr(datasets, "rig");

  return [
    { value: durationDol != null ? `${fmtNumber(durationDol)} d` : "—", labelKey: "ws.kpi.duration" },
    { value: fmtMoney(cost), labelKey: "ws.kpi.cost" },
    { value: finalMd != null ? `${fmtNumber(finalMd)} m` : "—", labelKey: "ws.kpi.finalMd" },
    { value: rig ?? "—", labelKey: "ws.kpi.rig" },
  ];
}

/** A point on the depth-vs-days plan curve (cumulative days → cumulative MD). */
export interface CurvePoint {
  days: number;
  md: number;
  phase: string;
}

/**
 * Fold the programa into a cumulative (days, MD) curve — the depth-vs-days plan
 * curve the wireframe draws. Each phase advances cumulative DOL and lands at its
 * `depth_until`. Returns [] when there's no program (component shows an empty
 * note instead of an axis-less chart).
 */
export function planCurve(program: unknown): CurvePoint[] {
  const steps = asArray<ProgramaStep>(program);
  if (steps.length === 0) return [];
  const points: CurvePoint[] = [{ days: 0, md: steps[0]?.depth_from ?? 0, phase: "" }];
  let days = 0;
  for (const step of steps) {
    days += step.dol ?? 0;
    points.push({ days, md: step.depth_until ?? points[points.length - 1]!.md, phase: step.phase });
  }
  return points;
}
